import { unwrap } from "@sagot/api-client";
import { CATALOGUE_GATED_ADDONS, SELF_SERVE_ADDONS, type FeatureAddonKey } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Badge, Icon, toast, type IconName } from "@sagot/ui";
import { useMutation } from "@tanstack/react-query";
import { useErrorMessage } from "../../../lib/api-errors";
import { useApi } from "../../../session/session-provider";
import { addonPriceOf, useAddonCatalog, useFormatters, yearContext, type BillingOverview } from "../shared";
import { heldUnits, launchTierOf, offeredFeatureAddons, unitsLeft } from "./billing-model";
import { SecureLaunchCompare } from "./secure-launch-compare";
import { GatedButton } from "./gated-button";
import { useInvalidateBilling } from "./use-billing";

type SelfServeAddon = (typeof SELF_SERVE_ADDONS)[number];
/** What the card can sell: the self-serve add-ons plus the feature add-ons the catalogue offers on this plan. */
export type OfferedAddon = SelfServeAddon | FeatureAddonKey;

const ICONS: Record<OfferedAddon, IconName> = {
  message_pack: "messages-square",
  extra_agent: "bot",
  extra_seat: "user-plus",
  remove_branding: "sparkles",
  knowledge_storage_1gb: "database",
  secure_launch_lite: "shield-check",
  secure_launch: "shield-check",
};

interface AddonsCardProps {
  overview: BillingOverview;
  canManage: boolean;
  onBuy: (addon: OfferedAddon) => void;
}

function ActiveAddons({ overview, canManage }: Omit<AddonsCardProps, "onBuy">) {
  const { t } = useTranslation();
  const api = useApi();
  const fmt = useFormatters();
  const message = useErrorMessage();
  const invalidate = useInvalidateBilling();
  const cancel = useMutation({
    mutationFn: (id: string) => unwrap(api.DELETE("/v1/billing/addons/{subscription_addon_id}", { params: { path: { subscription_addon_id: id } } })),
    onSuccess: async () => {
      toast.success(t("billing.addons.cancelled"));
      await invalidate();
    },
    onError: (caught) => toast.error(message(caught)),
  });
  if (!overview.addons.length) return <p className="px-5 py-4 text-sm text-fg-muted">{t("billing.addons.none")}</p>;
  return (
    <ul className="divide-y divide-line">
      {overview.addons.map((addon) => (
        <li key={addon.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold">
              {addon.name} {addon.quantity > 1 ? <span className="text-fg-subtle">× {addon.quantity}</span> : null}
            </div>
            <div className="text-xs text-fg-subtle">
              {addon.interval_price === null ? "—" : fmt.money(Number(addon.interval_price) * addon.quantity, addon.currency)} {t(overview.interval === "annual" ? "billing.perYear" : "billing.perMonth")}
              {addon.ends_at ? ` · ${t("billing.addons.endsOn", { date: fmt.date(addon.ends_at) })}` : ""}
            </div>
          </div>
          {addon.ends_at ? (
            <Badge tone="warning">{t("billing.addons.ending")}</Badge>
          ) : (
            <GatedButton allowed={canManage} variant="ghost" size="sm" loading={cancel.isPending && cancel.variables === addon.id} onClick={() => { cancel.mutate(addon.id); }}>
              {t("billing.addons.cancel")}
            </GatedButton>
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * Self-serve add-ons in the order the page lists them. Catalogue-gated ones (extra knowledge
 * storage) show only while an admin has switched them on and the workspace's plan is one they are
 * sold on, so a plan or an unapproved price never leads to a dead-end dialog.
 */
function useListedAddons(overview: BillingOverview): readonly SelfServeAddon[] {
  const catalog = useAddonCatalog().data;
  return SELF_SERVE_ADDONS.filter((key) => {
    if (!CATALOGUE_GATED_ADDONS.includes(key)) return true;
    const listed = catalog?.find((addon) => addon.key === key);
    return listed?.available_plans.includes(overview.plan.key) ?? false;
  });
}

/** Add-ons on this subscription + self-serve purchases (message packs, agents, seats, branding, knowledge storage) and the Secure launch add-ons the catalogue offers on this plan. */
export function AddonsCard({ overview, canManage, onBuy }: AddonsCardProps) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const catalog = useAddonCatalog().data ?? [];
  const listed = useListedAddons(overview);
  // The price for the subscription's interval: a year of an annual subscription is 10 months (DEC-135).
  const priceOf = (key: string) => {
    const found = catalog.find((addon) => addon.key === key);
    return found ? addonPriceOf(found) : undefined;
  };
  const priced = (key: string) => {
    const found = priceOf(key);
    return found ? fmt.money(found.intervalPrice, found.currency) : "—";
  };
  const features = offeredFeatureAddons(catalog, overview.plan.key, overview.features);
  const offered: OfferedAddon[] = [...listed, ...features];
  const tier = launchTierOf(overview.features);
  const lite = catalog.find((addon) => addon.key === "secure_launch_lite");
  const plus = catalog.find((addon) => addon.key === "secure_launch");
  const sold = (row: typeof lite) => row !== undefined && (row.available_plans.length === 0 || row.available_plans.includes(overview.plan.key));
  const compare = sold(lite) && sold(plus) && overview.plan.key !== "institution";
  const upgrade = (key: OfferedAddon) => key === "secure_launch" && tier === "lite";
  return (
    <section className="card overflow-hidden" aria-labelledby="addons-title">
      <div className="card-header">
        <div>
          <h2 id="addons-title" className="card-title">
            {t("billing.addons.title")}
          </h2>
          <p className="mt-0.5 text-xs text-fg-subtle">{t("billing.addons.subtitle")}</p>
        </div>
      </div>
      <ActiveAddons overview={overview} canManage={canManage} />
      <div className={`grid grid-cols-1 gap-3 border-t border-line p-5 md:grid-cols-2 ${offered.length > 4 ? "xl:grid-cols-3" : "xl:grid-cols-4"}`}>
        {offered.map((key) => {
          const left = unitsLeft(key, heldUnits(overview.addons, key));
          return (
            <div key={key} className="flex items-start gap-3 rounded-md border border-line p-3" data-addon={key}>
              <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-md bg-primary-soft text-primary-ink">
                <Icon name={ICONS[key]} className="size-4" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold">{t(`billing.addons.name_${key}`)}</div>
                <div className="text-xs text-fg-subtle">{t(`billing.addons.price_${key}`, { price: priced(key), ...yearContext(priceOf(key)?.per ?? null) })}</div>
                {left === 0 ? <div className="mt-1 text-xs text-fg-muted">{t("billing.addons.limitReached")}</div> : null}
              </div>
              <GatedButton allowed={canManage} variant="secondary" size="sm" disabled={left === 0} onClick={() => { onBuy(key); }}>
                {t(upgrade(key) ? "billing.addons.upgrade" : "billing.addons.add")}
                <span className="sr-only"> {t(`billing.addons.name_${key}`)}</span>
              </GatedButton>
            </div>
          );
        })}
      </div>
      {compare ? (
        <div className="border-t border-line p-5">
          <SecureLaunchCompare
            lite={{ monthly: lite?.price ?? null, currency: lite?.currency ?? overview.currency }}
            plus={{ monthly: plus?.price ?? null, currency: plus?.currency ?? overview.currency }}
            current={tier}
          />
        </div>
      ) : null}
    </section>
  );
}
